// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import React from 'react';
import { FinertyMark } from '@loa/ui-components';

interface PoweredByFinertyMarkProps {
  /** Size variant for different contexts */
  size?: 'small' | 'medium' | 'large';
  /** Position for absolute positioning (optional) */
  position?: 'bottom-left' | 'bottom-right' | 'bottom-center';
  /** Custom class name */
  className?: string;
}

const sizeToMark = {
  small: 28,
  medium: 32,
  large: 40,
};

const sizeConfig = {
  small: { padding: '6px 12px', gap: '6px' },
  medium: { padding: '8px 16px', gap: '8px' },
  large: { padding: '10px 20px', gap: '10px' },
};

/**
 * "Powered by Finerty" badge using the standalone constellation mark.
 * Mirrors {@link PoweredByFinerty} but renders the new square mark instead
 * of the wordmark — used in compact areas like the sidebar footer.
 */
export const PoweredByFinertyMark: React.FC<PoweredByFinertyMarkProps> = ({
  size = 'small',
  position,
  className = '',
}) => {
  const config = sizeConfig[size];

  const positionStyles: React.CSSProperties = position
    ? {
        position: 'absolute',
        ...(position === 'bottom-left' && { bottom: '20px', left: '20px' }),
        ...(position === 'bottom-right' && { bottom: '20px', right: '20px' }),
        ...(position === 'bottom-center' && { bottom: '20px', left: '50%', transform: 'translateX(-50%)' }),
      }
    : {};

  return (
    <div
      className={`powered-by-finerty-mark ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: config.gap,
        padding: config.padding,
        ...positionStyles,
      }}
    >
      <FinertyMark size={sizeToMark[size]} aria-label="Powered by Finerty" />
    </div>
  );
};

export default PoweredByFinertyMark;
